/**
 * Employee Service
 *
 * API client for employee (driver) operations.
 * Uses the /api/v1/ops/employees endpoints (JWT authenticated).
 */
import { api } from './api'
import type {
  Employee,
  EmployeeFilters,
  ApiResponse,
  PaginatedResponse,
} from '../types/employee'
import type { BulkResult } from '../types/dashboard'
import type { TrainingSettings } from '../types/training-plan'

/**
 * Employee service for ops frontend
 */
export const employeeService = {
  /**
   * List employees for a client
   */
  async listByClient(filters: EmployeeFilters): Promise<PaginatedResponse<Employee>> {
    const params = new URLSearchParams()
    params.set('client_id', filters.client_id)
    if (filters.status) params.set('status', filters.status)
    if (filters.page) params.set('page', filters.page.toString())
    if (filters.per_page) params.set('per_page', filters.per_page.toString())

    const response = await api.get<ApiResponse<PaginatedResponse<Employee>>>(
      `/v1/ops/employees?${params.toString()}`
    )

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch employees')
    }

    return response.data.data
  },

  /**
   * Get a single employee by ID
   */
  async getById(employeeId: string): Promise<Employee> {
    const response = await api.get<ApiResponse<Employee>>(
      `/v1/ops/employees/${employeeId}`
    )

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch employee')
    }

    return response.data.data
  },

  /**
   * Update a specific field group on an employee (inline popover editing)
   */
  async updateField(employeeId: string, field: string, values: Record<string, unknown>): Promise<{ message: string }> {
    const response = await api.patch<ApiResponse<{ message: string }>>(
      `/v1/portal/employees/${employeeId}/field`,
      { field, values }
    )

    if (!response.data.success) {
      throw new Error(response.data.message || response.data.error?.message || 'Failed to update field')
    }

    return response.data.data!
  },

  /** Staff-only training settings group; the server audits and reconciles it. */
  async updateTrainingSettings(
    employeeId: string,
    values: Partial<TrainingSettings>
  ): Promise<{ message: string }> {
    return this.updateField(employeeId, 'training_settings', values)
  },

  /**
   * Bulk change status for multiple employees
   */
  async bulkChangeStatus(
    employeeIds: string[],
    newStatus: string,
    leaveDetails?: { leave_reason?: string; leave_started_date?: string; expected_return_date?: string }
  ): Promise<BulkResult> {
    const response = await api.post<ApiResponse<BulkResult>>(
      '/v1/portal/employees/bulk/status',
      { employee_ids: employeeIds, new_status: newStatus, ...(leaveDetails ?? {}) }
    )
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Bulk status change failed')
    }
    return response.data.data
  },

  /**
   * Bulk order annual MVRs for multiple employees
   */
  async bulkOrderMvr(employeeIds: string[]): Promise<BulkResult> {
    const response = await api.post<ApiResponse<BulkResult>>(
      '/v1/portal/employees/bulk/order-mvr',
      { employee_ids: employeeIds }
    )
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Bulk MVR order failed')
    }
    return response.data.data
  },

  /**
   * Bulk transition hiring status for multiple employees
   */
  async bulkTransition(employeeIds: string[], newHiringStatus: string): Promise<BulkResult> {
    const response = await api.post<ApiResponse<BulkResult>>(
      '/v1/portal/employees/bulk/transition',
      { employee_ids: employeeIds, new_hiring_status: newHiringStatus }
    )
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Bulk transition failed')
    }
    return response.data.data
  },

  /**
   * Bulk enable/disable MVR monitoring for multiple employees
   */
  async bulkMvrMonitoring(employeeIds: string[], enroll: boolean): Promise<BulkResult> {
    const response = await api.post<ApiResponse<BulkResult>>(
      '/v1/portal/employees/bulk/mvr-monitoring',
      { employee_ids: employeeIds, enroll }
    )
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Bulk monitoring change failed')
    }
    return response.data.data
  },

  /**
   * Full SSN for one driver. The server audit-logs every call (fail-closed),
   * so only fetch on an explicit user action (the SsnReveal eye) — never
   * eagerly, and never to populate a list.
   */
  async revealSsn(employeeId: string): Promise<{ ssn: string }> {
    const response = await api.get<ApiResponse<{ ssn: string }>>(
      `/v1/consultant/employees/${employeeId}/ssn`
    )
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch SSN')
    }
    return response.data.data
  },
}
